// The mandatory call-disposition prompt (BUILD_BRIEF §9.4, UX principle 5): when a call the signed-in
// member took ends, the telephony service reports it, the server pushes `comms.call` to the member and
// this form opens by itself — outcome, an optional note and the next follow-up in the same form, three
// taps at most on a phone (outcome · follow-up · Save; the follow-up is suggested from the outcome, so
// usually two). "Later" only snoozes it: unlogged calls come back after two minutes, on every page load
// and when the tab regains focus, and a pill keeps count until they are logged.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, cn, Dialog, Input, Textarea, toast } from '@tatra/ui';
import { PhoneCall } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { errorMessage, post } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { useRealtime } from '../../lib/realtime.js';
import {
  addDays,
  dayKey,
  duration,
  fromLocalInput,
  timeOfDay,
  useTimeZone,
  zonedInstant,
} from '../../lib/time.js';
import { type PendingCall, useCommsStatus, usePendingDispositions } from './api.js';

const SNOOZE_MS = 2 * 60_000;
/** Outcomes after which nobody needs to be called again. */
const CLOSING = /not interested|wrong number|already bought|do not call|junk/i;

interface CallPush {
  callId: string;
  leadId: string | null;
  status: string;
  dispositionRequired: boolean;
}

type FollowUp = 'none' | 'hours2' | 'tomorrow' | 'days3' | 'custom';

export function DispositionPrompt() {
  const { can } = useAuth();
  const enabled = can('comms.call.read') && can('comms.call.place');
  const qc = useQueryClient();
  const pending = usePendingDispositions(enabled);
  const [snoozed, setSnoozed] = useState<Record<string, number>>({});
  const [now, setNow] = useState(() => Date.now());

  // A snoozed call comes back after two minutes.
  useEffect(() => {
    if (!Object.keys(snoozed).length) return;
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, [snoozed]);

  useRealtime<CallPush>('comms.call', (e) => {
    if (e.status === 'ringing' || e.status === 'in_progress') {
      if (e.status === 'in_progress') toast.success('Call connected', { id: `call-${e.callId}` });
      return;
    }
    // The call ended: refresh the queue; an answered call opens the form at once.
    void qc.invalidateQueries({ queryKey: ['calls'] });
    if (e.leadId) void qc.invalidateQueries({ queryKey: ['lead-timeline', e.leadId] });
    if (e.dispositionRequired) {
      setSnoozed((s) => {
        const next = { ...s };
        delete next[e.callId];
        return next;
      });
    } else {
      toast.info(
        e.status === 'missed' || e.status === 'no_answer' ? 'The call was not answered' : 'The call ended',
        {
          id: `call-${e.callId}`,
        },
      );
    }
  });

  const queue = (pending.data ?? []).filter((c) => snoozed[c.id] !== Number.POSITIVE_INFINITY);
  const due = queue.filter((c) => !snoozed[c.id] || snoozed[c.id]! <= now);
  const current = due[0] ?? null;
  const later = (id: string) => setSnoozed((s) => ({ ...s, [id]: Date.now() + SNOOZE_MS }));

  if (!enabled) return null;
  return (
    <>
      {queue.length > 0 && !current && (
        <button
          type="button"
          data-testid="pending-dispositions"
          className="fixed bottom-[calc(env(safe-area-inset-bottom)+4.75rem)] left-4 z-30 flex min-h-11 items-center gap-2 rounded-full border border-warning/50 bg-warning-bg px-4 text-sm font-medium text-warning shadow-lg lg:bottom-6"
          onClick={() =>
            setSnoozed((s) =>
              Object.fromEntries(Object.entries(s).filter(([, until]) => until === Number.POSITIVE_INFINITY)),
            )
          }
        >
          <PhoneCall className="h-4 w-4" aria-hidden="true" />
          {queue.length === 1 ? '1 call to log' : `${queue.length} calls to log`}
        </button>
      )}
      {current && (
        <DispositionDialog
          key={current.id}
          call={current}
          remaining={due.length - 1}
          onLater={() => later(current.id)}
          onDone={() => {
            // Out of the queue at once; the refetch below confirms it.
            setSnoozed((s) => ({ ...s, [current.id]: Number.POSITIVE_INFINITY }));
            void qc.invalidateQueries({ queryKey: ['calls'] });
            void qc.invalidateQueries({ queryKey: ['today'] });
            void qc.invalidateQueries({ queryKey: ['tasks'] });
            void qc.invalidateQueries({ queryKey: ['interests'] });
            if (current.lead) {
              void qc.invalidateQueries({ queryKey: ['lead-timeline', current.lead.id] });
              void qc.invalidateQueries({ queryKey: ['lead', current.lead.id] });
            }
          }}
        />
      )}
    </>
  );
}

function DispositionDialog({
  call,
  remaining,
  onLater,
  onDone,
}: {
  call: PendingCall;
  remaining: number;
  onLater: () => void;
  onDone: () => void;
}) {
  const tz = useTimeZone();
  const status = useCommsStatus();
  const outcomes = status.data?.telephony.dispositions ?? [];
  const [outcome, setOutcome] = useState<string | null>(null);
  const [followUp, setFollowUp] = useState<FollowUp>('tomorrow');
  const [custom, setCustom] = useState('');
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);

  const options = useMemo(() => {
    const today = dayKey(new Date(), tz);
    return {
      hours2: new Date(Date.now() + 2 * 3_600_000),
      tomorrow: zonedInstant(addDays(today, 1), 11 * 60, tz),
      days3: zonedInstant(addDays(today, 3), 11 * 60, tz),
    };
  }, [tz]);

  const pick = (o: string) => {
    setOutcome(o);
    // Suggest the follow-up the outcome usually needs; the member can change it with one tap.
    setFollowUp(CLOSING.test(o) ? 'none' : /call back|price|busy/i.test(o) ? 'hours2' : 'tomorrow');
  };

  const nextAt =
    followUp === 'none'
      ? null
      : followUp === 'custom'
        ? fromLocalInput(custom, tz)
        : options[followUp].toISOString();

  const save = useMutation({
    mutationFn: () =>
      post(`/calls/${call.id}/disposition`, {
        outcome,
        notes: notes.trim() || null,
        nextFollowUpAt: nextAt,
      }),
    onSuccess: () => {
      toast.success(
        nextAt ? `Logged · follow-up ${timeOfDay(nextAt, tz)} on ${dayKey(nextAt, tz)}` : 'Logged',
      );
      onDone();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const who = call.lead?.name ?? 'the caller';
  const project = call.lead?.interests[0]?.project.name;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onLater();
      }}
      title={`How did the call with ${who} go?`}
      description={
        <span>
          {call.direction === 'INBOUND' ? 'Incoming' : 'Outgoing'} call
          {call.talkSeconds ? `, ${duration(call.talkSeconds)}` : ''}
          {project ? ` · ${project}` : ''}
          {remaining > 0 ? ` · ${remaining} more to log` : ''}
        </span>
      }
    >
      <form
        className="space-y-4"
        data-testid="disposition-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (outcome && !(followUp === 'custom' && !nextAt)) save.mutate();
        }}
      >
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-text-muted">Outcome</legend>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Outcome">
            {outcomes.map((o) => (
              <button
                key={o}
                type="button"
                role="radio"
                aria-checked={outcome === o}
                onClick={() => pick(o)}
                className={cn(
                  'min-h-11 rounded-full border px-3.5 text-sm md:min-h-9',
                  outcome === o
                    ? 'border-primary bg-primary text-primary-fg'
                    : 'border-border bg-surface text-text hover:bg-surface-hover',
                )}
              >
                {o}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="mb-2 text-sm font-medium text-text-muted">Next follow-up</legend>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Next follow-up">
            {(
              [
                ['none', 'None'],
                ['hours2', 'In 2 hours'],
                ['tomorrow', 'Tomorrow 11:00'],
                ['days3', 'In 3 days'],
                ['custom', 'Pick a time'],
              ] as [FollowUp, string][]
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={followUp === k}
                onClick={() => setFollowUp(k)}
                className={cn(
                  'min-h-11 rounded-full border px-3.5 text-sm md:min-h-9',
                  followUp === k
                    ? 'border-primary bg-primary/15 font-medium text-primary'
                    : 'border-border bg-surface text-text hover:bg-surface-hover',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          {followUp === 'custom' && (
            <Input
              type="datetime-local"
              aria-label="Follow-up date and time"
              className="mt-2"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
            />
          )}
        </fieldset>

        {showNotes ? (
          <Textarea
            rows={2}
            maxLength={2000}
            aria-label="Note about the call"
            placeholder="What was agreed? (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        ) : (
          <button
            type="button"
            className="min-h-11 text-sm font-medium text-primary md:min-h-8"
            onClick={() => setShowNotes(true)}
          >
            Add a note
          </button>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={onLater}>
            Later
          </Button>
          <Button
            type="submit"
            size="lg"
            disabled={!outcome || (followUp === 'custom' && !nextAt)}
            loading={save.isPending}
          >
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
